// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/FAQSection.js

import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2 } from 'react-icons/fi';

const FAQSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  const faq = websiteData?.faq || { items: [] };

  // Local state vectors to completely isolate inputs during typing transactions
  const [localTitle, setLocalTitle] = useState(faq.sectionTitle || '');
  const [localSubtitle, setLocalSubtitle] = useState(faq.sectionSubtitle || '');
  const [localDisplayStyle, setLocalDisplayStyle] = useState(faq.displayStyle || 'accordion');
  const [localCheckboxes, setLocalCheckboxes] = useState({
    showCategories: faq.showCategories !== false,
    showSearch: faq.showSearch || false
  });
  const [localItems, setLocalItems] = useState(faq.items || []);

  const [newItem, setNewItem] = useState({
    question: '',
    answer: '',
    category: 'General'
  });
  const [saving, setSaving] = useState(false);

  // Sync isolated state fields from parent only on clean mount or tenant switches
  useEffect(() => {
    console.log('[FAQSection] Syncing local input metrics with parent props values baseline.');
    const sec = websiteData?.faq || {};
    setLocalTitle(sec.sectionTitle || '');
    setLocalSubtitle(sec.sectionSubtitle || '');
    setLocalDisplayStyle(sec.displayStyle || 'accordion');
    setLocalCheckboxes({
      showCategories: sec.showCategories !== false,
      showSearch: sec.showSearch || false
    });
    setLocalItems(sec.items || []);
  }, [websiteData?.faq, tenantId]);

  const handleToggle = () => {
    handleInputChange('faq', 'isActive', !faq.isActive);
  };

  const handleGlobalFieldChange = (field, value) => {
    console.log(`[FAQSection] Shifting parameter locally: Key [${field}] -> Value [${value}]`);
    if (field === 'sectionTitle') setLocalTitle(value);
    if (field === 'sectionSubtitle') setLocalSubtitle(value);
    if (field === 'displayStyle') setLocalDisplayStyle(value);
  };

  const handleLocalCheckboxToggle = (key) => {
    setLocalCheckboxes(prev => {
      const nextState = { ...prev, [key]: !prev[key] };
      console.log(`[FAQSection] Adjusting configuration switch locally [${key}]:`, nextState[key]);
      return nextState;
    });
  };

  const handleAddItem = () => {
    if (!newItem.question || !newItem.answer) return;
    
    const item = {
      _id: Date.now().toString(),
      ...newItem
    };
    
    const updatedItems = [...localItems, item];
    console.log('[FAQSection] Appending fresh FAQ question row entry locally:', item);
    setLocalItems(updatedItems);
    
    // Sync structural layout arrays instantly to prevent baseline mapping sync discrepancies
    handleInputChange('faq', 'items', updatedItems);
    setNewItem({ question: '', answer: '', category: 'General' });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = localItems.filter((_, i) => i !== index);
    console.log('[FAQSection] Extracting faq question block row entry at local index position:', index);
    setLocalItems(updatedItems);
    
    // Sync structural layout arrays instantly to prevent baseline mapping sync discrepancies
    handleInputChange('faq', 'items', updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    console.log(`[FAQSection] row item text change tracking index position: [${index}] -> Key [${field}]`);
    setLocalItems(updatedItems);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[FAQSection] Final Save Button Pressed. Shipping local memory updates up to parent container state maps.');
    
    try {
      // Synchronously push local parameters data up to master context tree configurations map
      await handleInputChange('faq', 'sectionTitle', localTitle);
      await handleInputChange('faq', 'sectionSubtitle', localSubtitle);
      await handleInputChange('faq', 'displayStyle', localDisplayStyle);
      await handleInputChange('faq', 'showCategories', localCheckboxes.showCategories);
      await handleInputChange('faq', 'showSearch', localCheckboxes.showSearch);
      await handleInputChange('faq', 'items', localItems);

      const assembledPayload = {
        ...faq,
        sectionTitle: localTitle,
        sectionSubtitle: localSubtitle,
        displayStyle: localDisplayStyle,
        showCategories: localCheckboxes.showCategories,
        showSearch: localCheckboxes.showSearch,
        items: localItems
      };

      await handleSave('faq', assembledPayload);
    } catch (error) {
      console.error('Failed to save faq section:', error);
    } finally {
      setSaving(false);
    }
  };

  const categories = ['General', 'Admissions', 'Academics', 'Fees', 'Scholarships', 'Campus Life', 'Placements', 'Other'];

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Top Header Action Toolbar Card Segment */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">FAQ Section</h2>
            <p className="text-sm text-slate-500 mt-0.5">Manage Frequently Asked Questions layout lists and visibility filters</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button
              type="button"
              onClick={handleToggle}
              className={`flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                faq.isActive !== false
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              {faq.isActive !== false ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleSaveClick}
              disabled={saving}
              className={`flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Configurations Parameters Settings Card layout */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Section Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
              <input
                type="text"
                value={localTitle}
                onChange={(e) => handleGlobalFieldChange('sectionTitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Frequently Asked Questions"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle</label>
              <input
                type="text"
                value={localSubtitle}
                onChange={(e) => handleGlobalFieldChange('sectionSubtitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Find answers to common questions"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Display Layout Alignment</label>
              <select
                value={localDisplayStyle}
                onChange={(e) => handleGlobalFieldChange('displayStyle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none"
              >
                <option value="accordion">Accordion Collapse Layout</option>
                <option value="grid">Grid Split Block View</option>
              </select>
            </div>

            <div className="md:col-span-2 bg-slate-50 rounded-xl p-3 border border-slate-150 grid grid-cols-2 gap-3">
              {[
                { label: 'Show Categories Filter', key: 'showCategories', checked: localCheckboxes.showCategories },
                { label: 'Show Live Search Bar', key: 'showSearch', checked: localCheckboxes.showSearch },
              ].map((checkbox) => (
                <label key={checkbox.key} className="flex items-center space-x-2.5 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={checkbox.checked}
                    onChange={() => handleLocalCheckboxToggle(checkbox.key)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all border ${
                    checkbox.checked 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'bg-white border-slate-300 text-transparent group-hover:border-slate-400'
                  }`}>
                    <span className={`w-2 h-2 rounded-sm bg-white ${checkbox.checked ? 'block' : 'hidden'}`} />
                  </div>
                  <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                    {checkbox.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Added Questions Loops List Segment block */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-sm font-bold text-slate-800">Questions</h3>
            <span className="text-xs font-semibold bg-slate-200 text-slate-600 px-2.5 py-0.5 rounded-full">
              {localItems.length} Added
            </span>
          </div>

          <div className="space-y-4">
            {localItems.map((item, index) => (
              <div key={item._id || index} className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                <div className="p-4 sm:p-5 space-y-3.5">
                  <div className="grid grid-cols-1 gap-3">
                    
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Question Title Text</span>
                      <input
                        type="text"
                        value={item.question || ''}
                        onChange={(e) => handleItemChange(index, 'question', e.target.value)}
                        placeholder="Question Headline"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                      />
                    </div>

                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Detailed Answer Explanation Description</span>
                      <textarea
                        value={item.answer || ''}
                        onChange={(e) => handleItemChange(index, 'answer', e.target.value)}
                        placeholder="Provide detailed analytical answers here..."
                        rows="2"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500 resize-y"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2 border-t border-slate-100">
                    <div className="flex items-center space-x-2 bg-slate-150 rounded-lg border border-slate-200 p-1 min-w-[140px]">
                      <span className="text-[10px] font-bold uppercase text-slate-400 px-1">Scope Tag:</span>
                      <select
                        value={item.category || 'General'}
                        onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                        className="bg-transparent text-xs font-semibold text-slate-700 outline-none flex-1 py-0.5 cursor-pointer appearance-none"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveItem(index)}
                      className="text-rose-500 hover:text-rose-700 font-semibold text-xs flex items-center px-2 py-1 rounded-md hover:bg-rose-50 transition-colors self-end sm:self-auto"
                    >
                      <FiTrash2 className="mr-1 h-3.5 w-3.5" />
                      Delete Question
                    </button>
                  </div>

                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add Fresh FAQ Entry Form Block container */}
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1 bg-blue-50 rounded-md text-blue-600">
              <FiPlus className="h-4 w-4 stroke-[3]" />
            </div>
            <h4 className="text-sm font-bold text-slate-800">Create New FAQ Item</h4>
          </div>

          <div className="space-y-3">
            <input
              type="text"
              value={newItem.question}
              onChange={(e) => setNewItem({ ...newItem, question: e.target.value })}
              placeholder="Question Title Header *"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
            />
            
            <textarea
              value={newItem.answer}
              onChange={(e) => setNewItem({ ...newItem, answer: e.target.value })}
              placeholder="Answer Body content details * (Supports descriptive text explanations)"
              rows="2"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 resize-y"
            />
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
              <div className="flex items-center space-x-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200 inline-flex self-start">
                <span className="text-xs font-semibold text-slate-500">Group Category:</span>
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                  className="bg-transparent text-xs font-bold text-slate-700 outline-none cursor-pointer"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleAddItem}
                className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2 bg-slate-900 text-white font-semibold rounded-lg text-sm hover:bg-slate-800 transition-all active:scale-[0.98] shadow-sm whitespace-nowrap"
              >
                <FiPlus className="mr-1.5 h-4 w-4" />
                Insert Item
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FAQSection;